import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface VerificationResult {
  verified: boolean;
  paymentStatus: string;
  packageName: string;
  customerEmail: string;
  orderReference: string;
  orderId: string;
  amountGbp: number;
  onboardingSubmitted: boolean;
}

const CyberEssentialsMicroSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verification, setVerification] = useState<VerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get("session_id");
      
      if (!sessionId) {
        setError("No session ID provided");
        setLoading(false);
        return;
      }

      try {
        const { data, error: fnError } = await supabase.functions.invoke("verify-ce-payment", {
          body: { sessionId },
        });

        if (fnError) throw fnError;
        setVerification(data);
      } catch (err) {
        console.error("Verification error:", err);
        setError("Failed to verify payment. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            {loading ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-12 h-12 mx-auto animate-spin text-accent mb-4" />
                  <p className="text-muted-foreground">Verifying your payment...</p>
                </CardContent>
              </Card>
            ) : error ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
                  <h2 className="text-xl font-bold mb-2">Verification Failed</h2>
                  <p className="text-muted-foreground mb-6">{error}</p>
                  <Button onClick={() => navigate("/services/cyber-essentials-micro")}>
                    Return to packages
                  </Button>
                </CardContent>
              </Card>
            ) : verification?.paymentStatus === "paid" ? (
              <Card>
                <CardHeader className="text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <CardTitle className="text-2xl">Payment Successful!</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-secondary/30 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Package:</span>
                      <span className="font-medium">{verification.packageName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">£{(verification.amountGbp / 100).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-medium">{verification.customerEmail}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order Reference:</span>
                      <span className="font-mono text-sm">{verification.orderReference.slice(0, 8)}...</span>
                    </div>
                  </div>

                  {verification.onboardingSubmitted ? (
                    <div className="text-center">
                      <p className="text-green-600 font-medium mb-4">
                        ✓ Onboarding already submitted
                      </p>
                      <p className="text-muted-foreground">
                        We'll be in touch within 1 working day.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h3 className="font-semibold mb-2">Next Step: Complete Onboarding</h3>
                      <p className="text-muted-foreground mb-4">
                        Please complete a short form (5 minutes) so we can start working on your certification.
                      </p>
                      <Button 
                        className="w-full bg-accent hover:bg-accent/90"
                        onClick={() => navigate(`/services/cyber-essentials-micro/onboarding?order_id=${verification.orderId}`)}
                      >
                        Complete Onboarding Form
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                  <h2 className="text-xl font-bold mb-2">Payment Pending</h2>
                  <p className="text-muted-foreground mb-6">
                    Your payment is still processing. Please check back shortly or contact support.
                  </p>
                  <Button onClick={() => navigate("/services/cyber-essentials-micro")}>
                    Return to packages
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CyberEssentialsMicroSuccess;
